import { withWindow } from '@shared/utils/withWindow';
import { getLocalStorage } from '@shared/core/storage';
import type React from 'react';

/**
 * Changes to button IDs must be coordinated with the native apps
 */
type WellKnownButtonId = 'SetupAddToJoyButton' | 'ContactCollectorEditGuestList' | 'ContactCollectorCopyLink';

type MessageType = 'ButtonClickEvent' | 'NavigateToTab' | 'Navigate'; // add new event types here like CheckBoxSelectedEvent etc.

type ButtonClickEventData = Readonly<{
  componentAction: 'Clicked';
  componentId: WellKnownButtonId;
  componentType: 'Button';
  url?: string;
}>;

type NavigateToTabData = Readonly<{
  tabName: 'addGifts';
}>;

type NavigateDeeplinkData = Readonly<{
  url: string;
}>;

type PostMessagePayload = Readonly<
  {
    signature: 'JoyWebViewToNativeMessage';
    type: MessageType;
  } & (
    | {
        type: 'ButtonClickEvent';
        data: ButtonClickEventData;
      }
    | {
        type: 'NavigateToTab';
        data: NavigateToTabData;
      }
    | {
        type: 'Navigate';
        data: NavigateDeeplinkData;
      }
  )
>;

export const webViewPostMessage = (message: PostMessagePayload) => {
  withWindow(global => {
    // iOS
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (global as any).webkit?.messageHandlers?.iOSHandler?.postMessage?.(message);

    //Android
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (global as any).JoyAndroid?.postMessage?.(message);
  });
};

export const useNativeState = (): Readonly<{
  isNative: boolean;
  appVersion: string | null;
}> => {
  return withWindow(
    () => {
      // Read through getLocalStorage(), which catches blocked-storage access. Optional chaining
      // on `global.localStorage` cannot help here: when the browser blocks storage the property
      // GETTER itself throws SecurityError, and this hook runs during render — so the throw took
      // down the whole guest site via the error boundary. See PRNT-4827.
      const localStorage = getLocalStorage();
      return {
        isNative: !!localStorage.getItem('isNative'),
        appVersion: localStorage.getItem('appVersion') ?? null,
      };
    },
    {
      isNative: false,
      appVersion: '0.0.0',
    },
  );
};

export const useButtonClickHandler = (
  options: Readonly<{
    buttonId: WellKnownButtonId;
    handler: React.MouseEventHandler;
  }>,
): React.MouseEventHandler => {
  const { isNative } = useNativeState();
  return isNative
    ? e => {
        e.preventDefault();
        webViewPostMessage({
          signature: 'JoyWebViewToNativeMessage',
          type: 'ButtonClickEvent',
          data: {
            componentAction: 'Clicked',
            componentId: options.buttonId,
            componentType: 'Button',
          },
        });
        // per Orest:
        // the mobile app will never respond to both messages at the same time:
        // - old clients will only respond to the old message type (element based)
        // - new updated clients will only respond to the new message type (action based)
        // after we reach a adoption in the new clients we can remove the old post message.
        // TO DO: remove the old call
        webViewPostMessage({
          signature: 'JoyWebViewToNativeMessage',
          type: 'NavigateToTab',
          data: {
            tabName: 'addGifts',
          },
        });
      }
    : options.handler;
};
